import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import Skeleton from "@material-ui/lab/Skeleton";
import Typography from "@material-ui/core/Typography";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import ScheduleIcon from "@material-ui/icons/Schedule";
import WarningIcon from "@material-ui/icons/Warning";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../../services/api";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { formatOsPrevisao } from "../../../utils/osDates";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1, 0, 2)
  },
  viewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column"
    }
  },
  headerInfo: {
    minWidth: 0
  },
  viewTitle: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    lineHeight: 1.2
  },
  viewSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: 4
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      width: "100%"
    }
  },
  technicianSelect: {
    minWidth: 220,
    [theme.breakpoints.down("xs")]: {
      flex: 1,
      minWidth: 0
    }
  },
  refreshButton: {
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      flex: 1
    }
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(160px, 1fr))",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(3, minmax(160px, 1fr))"
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(140px, 1fr))"
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  metricCard: {
    position: "relative",
    minHeight: 108,
    padding: theme.spacing(1.75, 2),
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    boxShadow: "none",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      width: 4,
      backgroundColor: props => props.accent
    }
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.35
  },
  metricValue: {
    color: theme.palette.text.primary,
    fontSize: 25,
    fontWeight: 700,
    lineHeight: 1.2,
    marginTop: theme.spacing(0.75)
  },
  metricDetail: {
    color: theme.palette.text.secondary,
    fontSize: 11,
    marginTop: 4
  },
  attentionSection: {
    marginTop: theme.spacing(3)
  },
  attentionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.error.dark,
    fontSize: 18,
    fontWeight: 800,
    marginBottom: theme.spacing(1)
  },
  attentionSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginBottom: theme.spacing(1.5)
  },
  attentionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  attentionCard: {
    overflow: "hidden",
    borderRadius: 12,
    boxShadow: "none"
  },
  inactiveCard: {
    border: `2px solid ${theme.palette.warning.main}`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255, 152, 0, 0.12)" : "#FFF8E1"
  },
  overdueCard: {
    border: `2px solid ${theme.palette.error.main}`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(244, 67, 54, 0.12)" : "#FFEBEE"
  },
  attentionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2)
  },
  attentionHeaderText: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 800
  },
  inactiveIcon: {
    color: theme.palette.warning.dark
  },
  overdueIcon: {
    color: theme.palette.error.dark
  },
  attentionList: {
    padding: 0
  },
  attentionItem: {
    display: "block",
    padding: theme.spacing(1.5, 2),
    textAlign: "left",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  itemTitle: {
    color: theme.palette.text.primary,
    fontSize: 13,
    fontWeight: 700
  },
  itemDetail: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginTop: 3
  },
  itemTime: {
    color: theme.palette.text.primary,
    fontSize: 11,
    fontWeight: 700,
    marginTop: 5
  },
  emptyAttention: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    padding: theme.spacing(2),
    textAlign: "center"
  },
  error: {
    padding: theme.spacing(3),
    textAlign: "center",
    border: `1px solid ${theme.palette.error.light}`,
    boxShadow: "none"
  },
  errorTitle: {
    fontWeight: 600,
    marginBottom: 6
  },
  errorDetail: {
    color: theme.palette.text.secondary,
    fontSize: 13
  },
  unconfigured: {
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.warning.light}`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255, 152, 0, 0.12)" : "#FFF8E1",
    boxShadow: "none"
  }
}));

const formatMinutes = value => {
  if (value === null || value === undefined) return "—";
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) return "—";
  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const remaining = rounded % 60;
  return hours ? `${hours}h ${remaining}min` : `${remaining}min`;
};

const formatDateTime = value => {
  if (!value) return "data não informada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "data não informada";
  return date.toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short"
  });
};

const errorMessage = error =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  "Não foi possível carregar a visão do técnico.";

const MetricCard = ({ label, value, detail, accent }) => {
  const classes = useStyles({ accent });

  return (
    <Paper className={classes.metricCard}>
      <div className={classes.metricLabel}>{label}</div>
      <div className={classes.metricValue}>{value}</div>
      {detail && <div className={classes.metricDetail}>{detail}</div>}
    </Paper>
  );
};

const AttentionList = ({ items, type, onOpen }) => {
  const classes = useStyles();
  const overdue = type === "overdue";
  const title = overdue ? "OS fora do prazo" : "Sem interação há mais de 1 dia";
  const Icon = overdue ? ErrorOutlineIcon : ScheduleIcon;

  return (
    <Paper
      className={`${classes.attentionCard} ${
        overdue ? classes.overdueCard : classes.inactiveCard
      }`}
    >
      <div className={classes.attentionHeader}>
        <div className={classes.attentionHeaderText}>
          <Icon className={overdue ? classes.overdueIcon : classes.inactiveIcon} />
          <span>{title}</span>
        </div>
        <strong>{items.length}</strong>
      </div>
      <Divider />
      {items.length ? (
        <List className={classes.attentionList} disablePadding>
          {items.map(item => (
            <React.Fragment key={item.id}>
              <ListItem
                button
                className={classes.attentionItem}
                onClick={() => onOpen(item.id)}
              >
                <ListItemText
                  primary={`OS #${item.id} · ${item.descricao_curta || "Sem descrição"}`}
                  secondary={
                    <>
                      <div>{item.cliente || "Cliente não informado"} · {item.status || "Status não informado"}</div>
                      <div className={classes.itemTime}>
                        {overdue ? "Prazo: " : "Última atividade: "}
                        {overdue
                          ? item.data_previsao_termino
                            ? formatOsPrevisao(item.data_previsao_termino)
                            : "data não informada"
                          : formatDateTime(item.timestamp)}
                      </div>
                    </>
                  }
                  primaryTypographyProps={{ className: classes.itemTitle }}
                  secondaryTypographyProps={{ className: classes.itemDetail }}
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      ) : (
        <div className={classes.emptyAttention}>Nenhuma OS nesta situação.</div>
      )}
    </Paper>
  );
};

const LoadingState = () => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.cardsGrid}>
        {[1, 2, 3, 4].map(item => (
          <Skeleton key={item} className={classes.metricCard} variant="rect" />
        ))}
      </div>
      <div className={classes.attentionSection}>
        <Skeleton variant="text" width={260} height={32} />
        <div className={classes.attentionGrid}>
          <Skeleton variant="rect" height={240} />
          <Skeleton variant="rect" height={240} />
        </div>
      </div>
    </>
  );
};

const TechnicianDayView = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";
  const ownTechnicianId = user?.IDTecnicoOS;
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTechnicians, setLoadingTechnicians] = useState(isAdmin);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAdmin) return undefined;

    let active = true;
    setLoadingTechnicians(true);
    api
      .get("/os/tecnicos", { params: { per_page: 200 } })
      .then(response => {
        if (!active) return;
        const items = response?.data?.data || [];
        setTechnicians(items);
        const ownId = String(ownTechnicianId || "");
        const initial = items.find(item => String(item.id) === ownId) || items[0];
        setSelectedTechnicianId(initial ? String(initial.id) : "");
      })
      .catch(requestError => {
        if (active) setError(errorMessage(requestError));
      })
      .finally(() => {
        if (active) setLoadingTechnicians(false);
      });

    return () => {
      active = false;
    };
  }, [isAdmin, ownTechnicianId]);

  const loadData = useCallback(
    async (refresh = false) => {
      if (isAdmin && !selectedTechnicianId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const params = isAdmin
          ? { technician_id: selectedTechnicianId, ...(refresh ? { refresh: 1 } : {}) }
          : refresh
            ? { refresh: 1 }
            : undefined;
        const response = await api.get("/dashboard/technician-day", { params });
        setData(response.data);
      } catch (requestError) {
        setError(errorMessage(requestError));
      } finally {
        setLoading(false);
      }
    },
    [isAdmin, selectedTechnicianId]
  );

  useEffect(() => {
    if (isAdmin && !selectedTechnicianId) {
      setLoading(false);
      return;
    }

    loadData();
  }, [isAdmin, selectedTechnicianId, loadData]);

  const openOrder = id => history.push(`/ordens-servico/${id}`);
  const metrics = data?.metrics;
  const technicianName = data?.technician?.name || "seu técnico";

  return (
    <div className={classes.root}>
      <div className={classes.viewHeader}>
        <div className={classes.headerInfo}>
          <Typography variant="h6" className={classes.viewTitle}>
            Visão do técnico
          </Typography>
          <div className={classes.viewSubtitle}>
            Acompanhe o desempenho e os pontos que precisam de atenção{data ? ` · ${technicianName}` : ""}.
          </div>
        </div>
        <div className={classes.headerActions}>
          {isAdmin && (
            <FormControl
              variant="outlined"
              size="small"
              className={classes.technicianSelect}
              disabled={loadingTechnicians}
            >
              <InputLabel id="technician-dashboard-label">Técnico</InputLabel>
              <Select
                labelId="technician-dashboard-label"
                value={selectedTechnicianId}
                onChange={event => setSelectedTechnicianId(event.target.value)}
                label="Técnico"
              >
                {technicians.map(technician => (
                  <MenuItem key={technician.id} value={String(technician.id)}>
                    {technician.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            className={classes.refreshButton}
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => loadData(true)}
            disabled={loading || loadingTechnicians || (isAdmin && !selectedTechnicianId)}
          >
            Reconsultar
          </Button>
        </div>
      </div>

      {!loading &&
      ((!isAdmin && !ownTechnicianId) ||
        (isAdmin && !selectedTechnicianId && !loadingTechnicians && !error)) ? (
        <Paper className={classes.unconfigured}>
          <Typography variant="subtitle1" gutterBottom>
            Técnico de OS não configurado
          </Typography>
          <Typography variant="body2">
            Solicite ao administrador a vinculação do seu usuário a um técnico de OS para visualizar seus indicadores.
          </Typography>
        </Paper>
      ) : (loading || loadingTechnicians) && !data ? (
        <LoadingState />
      ) : error && !data ? (
        <Paper className={classes.error}>
          <div className={classes.errorTitle}>Indicadores indisponíveis</div>
          <div className={classes.errorDetail}>{error}</div>
        </Paper>
      ) : data ? (
        <>
          <div className={classes.cardsGrid}>
            <MetricCard
              label="OS criadas hoje"
              value={metrics.os_today}
              detail="atribuídas ao técnico"
              accent="#2563EB"
            />
            <MetricCard
              label="Média diária de OS"
              value={metrics.os_daily_average_30_days ?? "—"}
              detail="últimos 30 dias"
              accent="#0F766E"
            />
            <MetricCard
              label="Tempo médio N1"
              value={formatMinutes(metrics.n1_average_minutes)}
              detail="OS concluídas · últimos 30 dias"
              accent="#7C3AED"
            />
            <MetricCard
              label="1ª resposta média"
              value={formatMinutes(metrics.first_response_average_minutes)}
              detail="N1 · últimos 30 dias"
              accent="#EA580C"
            />
          </div>

          <section className={classes.attentionSection}>
            <div className={classes.attentionTitle}>
              <WarningIcon />
              Pontos de atenção
            </div>
            <div className={classes.attentionSubtitle}>
              Estas OS exigem ação: não deixe o problema envelhecer.
            </div>
            <div className={classes.attentionGrid}>
              <AttentionList
                items={data.attention?.inactive || []}
                type="inactive"
                onOpen={openOrder}
              />
              <AttentionList
                items={data.attention?.overdue || []}
                type="overdue"
                onOpen={openOrder}
              />
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
};

export default TechnicianDayView;
